import React, { useMemo, useState } from "react";
import PasswordField from "./PasswordField";
import {
  calcPasswordStrength,
  strengthColor,
  strengthLabel,
} from "@/components/auth/utils/passwordStrength";

type Props = {
  onSubmit?: (payload: { oldPassword: string; newPassword: string }) => void | Promise<void>;
  submitting?: boolean;
};

export default function ChangePasswordForm({ onSubmit, submitting = false }: Props) {
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const strength = useMemo(() => calcPasswordStrength(newPwd), [newPwd]);
  const pwdMatch = newPwd === confirmPwd && newPwd.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Client validations (shown on top)
    if (!oldPwd || !newPwd || !confirmPwd) {
      setFormError("Please fill all fields.");
      return;
    }

    if (!pwdMatch) {
      setFormError("New passwords do not match.");
      return;
    }

    if (strength < 3) {
      setFormError("Please choose a stronger password.");
      return;
    }

    if (!onSubmit) return;

    try {
      await onSubmit({ oldPassword: oldPwd, newPassword: newPwd });

      // Reset fields after success
      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to change password.";
      setFormError(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
      {/* Top-level error message (all errors appear here) */}
      {formError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {formError}
        </div>
      )}

      {/* Old password */}
      <PasswordField
        label="Current password"
        value={oldPwd}
        onChange={setOldPwd}
        placeholder="••••••••"
        autoComplete="current-password"
      />

      {/* New password + strength bar */}
      <div>
        <PasswordField
          label="New password"
          value={newPwd}
          onChange={setNewPwd}
          placeholder="At least 8 characters"
          autoComplete="new-password"
        />

        <div className="mt-2">
          <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${(strength / 5) * 100}%`,
                backgroundColor: strengthColor(strength),
              }}
            />
          </div>

          <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
            <span>
              Strength:{" "}
              <span style={{ color: strengthColor(strength) }}>
                {strengthLabel(strength)}
              </span>
            </span>
            <span>Use Aa · 0-9 · !@#</span>
          </div>
        </div>
      </div>

      {/* Confirm */}
      <div>
        <PasswordField
          label="Confirm new password"
          value={confirmPwd}
          onChange={setConfirmPwd}
          placeholder="Re-enter new password"
          autoComplete="new-password"
        />
        {!pwdMatch && confirmPwd.length > 0 && (
          <p className="mt-1 text-[11px] text-red-600">Passwords don’t match</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="w-full rounded-xl bg-sky-500 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-60"
        disabled={submitting || !oldPwd || !pwdMatch || strength < 3}
      >
        {submitting ? "Updating…" : "Update Password"}
      </button>
    </form>
  );
}
